import { forwardRef, InputHTMLAttributes, ReactNode, useId } from 'react'

import * as LabelPrimitive from '@radix-ui/react-label'
import cn from 'clsx'

import s from './input.module.scss'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  variant?: 'default' | 'ghost' | 'filled'
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, label, error, helperText, leftIcon, rightIcon, variant = 'default', disabled, ...props },
    ref,
  ) => {
    const inputId = useId()
    return (
      <div className={s.wrapper}>
        {label && (
          <LabelPrimitive.Root htmlFor={inputId} className={cn(s.label, disabled && s.labelDisabled)}>
            {label}
          </LabelPrimitive.Root>
        )}

        <div className={s.inputContainer}>
          {leftIcon && <div className={s.iconLeft}>{leftIcon}</div>}

          <input
            {...props}
            type={type}
            id={inputId}
            className={cn(
              s.input,
              s[`variant${variant.charAt(0).toUpperCase() + variant.slice(1)}`],
              error && s.hasError,
              leftIcon && s.hasLeftIcon,
              rightIcon && s.hasRightIcon,
              className,
            )}
            ref={ref}
            disabled={disabled}
          />

          {rightIcon && <div className={s.iconRight}>{rightIcon}</div>}
        </div>

        {(error || helperText) && (
          <p className={cn(s.helperText, error ? s.helperTextError : s.helperTextMuted)}>{error || helperText}</p>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'

export { Input }
